





































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import TextInput from '../formComponents/core/TextInput.vue';
import { SearchSuggestion } from '../../contract/suggestion.contract';

type Indexed<T> = T & { _index: number };

@Component({})
export default class Typeahead<T extends SearchSuggestion> extends Vue {
  @Prop()
  public data!: T[] | Promise<T[]>;

  @Prop()
  public inputElement!: TextInput;

  public selectedIndex = -1;

  public dataContainer: T[] = [];

  public loading = false;

  public forceClosed = false;

  public get isOpen(): boolean {
    return (this.loading || (this.dataContainer && this.dataContainer.length > 0)) && !this.forceClosed;
  }

  private uniqeRef = `typeahead_ref`;

  public get scoredData(): Array<T & { score: number }> {
    return this.dataContainer.map((data, index) => ({ ...data, score: 1 / index }));
  }

  public get suggestionCategories(): string[] {
    return this.scoredData
      .map((data) => data.suggestionType)
      .filterDuplicates()
      .sort((cat1, cat2) => {
        const totalCategory1Score = this.scoredData
          .filter((data) => data.suggestionType === cat1)
          .reduce((acc, curr) => acc + curr.score, 0);
        const totalCategory2Score = this.scoredData
          .filter((data) => data.suggestionType === cat2)
          .reduce((acc, curr) => acc + curr.score, 0);
        return totalCategory2Score - totalCategory1Score;
      });
  }

  public get indexedSuggestions(): Array<Indexed<T>> {
    return this.suggestionCategories
      .reduce((acc, curr) => [...acc, ...this.suggestionsWithCategory(curr)], [] as T[])
      .map((sortedSuggestion, index) => ({ ...sortedSuggestion, _index: index }));
  }

  public indexedSuggestionsWithCategory(suggestionType: string): Array<Indexed<T>> {
    return this.indexedSuggestions.filter((indexedSuggestion) => indexedSuggestion.suggestionType === suggestionType);
  }

  public suggestionsWithCategory(suggestionType: string): T[] {
    return this.scoredData
      .filter((data) => data.suggestionType === suggestionType)
      .sort((d1, d2) => d2.score - d1.score);
  }

  @Watch('selectedIndex')
  public publishSelectedElement() {
    if (this.dataContainer && this.selectedIndex >= this.dataContainer.length) {
      return;
    }
    this.$emit(
      'activeEntryChanged',
      this.indexedSuggestions.find((data) => data._index === this.selectedIndex),
    );
  }

  @Watch('data', { deep: true })
  public async computeData() {
    if (this.data && (this.data as Promise<T[]>).then) {
      this.loading = true;
      this.dataContainer = (await this.data).map((data, index) => ({ ...data, _index: index }));
      this.loading = false;
    } else if (Array.isArray(this.data)) {
      this.dataContainer = this.data.map((data, index) => ({ ...data, _index: index }));
    }
    this.forceClosed = false;
  }

  @Watch('inputElement')
  public initInputElement() {
    // Inits the linked input element.
    this.inputElement.$on('keydown', this.onKeyDown);
    this.inputElement.$on('focusout', this.onFocusOut);
  }

  public selectItem(item: T) {
    this.forceClosed = true;
    this.$emit('select', item);
  }

  public focusUpOrInput(index: number) {
    if (index > 0) {
      this.focusEntryElement(index - 1);
    } else {
      this.focusOnInput();
    }
  }

  public hideIfNotEntry(event: FocusEvent) {
    // If the event does not have a related target, close the typeahead component.
    if (!event.relatedTarget) {
      this.forceCloseTypeahead();
      return;
    }
    // If the related target (the target which has focus after this event) is NOT an entry in the typeahead component
    // AND the related target is NOT the linked input element, close the typeahead component.
    if (
      !this.isEntryElement(event.relatedTarget as HTMLElement) &&
      this.inputElement !== ((event.relatedTarget as unknown) as TextInput)
    ) {
      this.forceCloseTypeahead();
    }
  }

  public focusOnInput() {
    this.inputElement.focus();
    // When focussing on the input, the selected index becomes -1
    this.selectedIndex = -1;
  }

  public typeaheadEntryRef(index: number): string {
    return `${this.uniqeRef}-${index}`;
  }

  // Checks if the given element is an entry in the typeahead component.
  private isEntryElement(element: HTMLElement): boolean {
    if (!element) {
      return false;
    }
    return this.dataContainer
      .map((_, index) => this.typeaheadEntryRef(index))
      .map((ref: string) => (this.$refs[ref] as Element[])[0])
      .some((typeaheadEntry) => element === typeaheadEntry);
  }

  // Is triggered when the linked inputelement loses focus.
  // REMARK: The parameter is typed as "Event" and not "FocusEvent" because typescript complains (unfairly) about this.
  private onFocusOut(event: Event): void {
    // If the input elment loses focus and the next focused element is NOT an entry element: close the typeahead element.
    if (!this.isEntryElement((event as FocusEvent).relatedTarget as HTMLElement)) {
      this.forceCloseTypeahead();
    }
  }

  private onKeyDown(event: KeyboardEvent) {
    const key = event.which || event.keyCode;
    if (key === 40) {
      // DownArrow key
      this.focusEntryElement(0);
      event.preventDefault();
    }
    if (key === 27 || key === 13) {
      this.forceCloseTypeahead();
    }
  }

  private focusEntryElement(index: number) {
    if (index < 0 || (this.dataContainer && index >= this.dataContainer.length)) {
      return;
    } else {
      const el = this.$refs[this.typeaheadEntryRef(index)] as any[];
      if (el && el[0]) {
        el[0].focus();
      }
    }
  }

  private forceCloseTypeahead() {
    // On escape or enter force close of typeahead and reset current selection
    this.forceClosed = true;
    this.selectedIndex = -1;
  }
}
